import { useEffect, useState, useCallback } from 'react';
import {
  RefreshCw, Trophy, TrendingUp, CheckCircle2, DollarSign, Users, BarChart3, XCircle,
} from 'lucide-react';
import { contractService } from './_shared/api';
import type { FollowupData, FollowupSeller } from './_shared/types';
import MonthSelector from './_components/MonthSelector';
import StackedBarChart from './_components/StackedBarChart';
import GrowthLineChart from './_components/GrowthLineChart';
import { formatBRL } from './_shared/utils/format';

/**
 * Prospect > Follow Up — acompanhamento mensal de vendas (Fase 27, porte
 * do web novo): resumo do time, cards por vendedor, barras de vendas por
 * vendedor no ano, linha de crescimento da base/faturamento e lista de
 * declinados com motivo.
 */

function currentMonthYM(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export default function FollowUpPage() {
  const [month, setMonth] = useState<string>(currentMonthYM());
  const [data, setData] = useState<FollowupData | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await contractService.followup(month));
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { void load(); }, [load]);

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-fg">Follow Up</h1>
          <p className="text-sm text-fg-2">Acompanhamento mensal de vendas</p>
        </div>
        <button
          onClick={() => void load()}
          disabled={loading}
          className="w-10 h-10 rounded-full bg-canvas hover:bg-hover border border-line flex items-center justify-center transition"
          aria-label="Atualizar"
        >
          <RefreshCw size={18} className={loading ? 'animate-spin text-primary' : 'text-fg-2'} />
        </button>
      </header>

      <div className="max-w-md mx-auto">
        <MonthSelector value={month} onChange={setMonth} />
      </div>

      {loading && !data && (
        <div className="py-12 flex justify-center">
          <RefreshCw size={28} className="animate-spin text-primary" />
        </div>
      )}

      {data && (
        <>
          {/* Resumo do mês */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 size={14} className="text-primary" />
              <h2 className="text-xs font-bold uppercase tracking-wider text-primary">
                Resumo do mês
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
              <BigCard
                icon={<CheckCircle2 size={14} className="text-success" />}
                label="Vendas aprovadas"
                value={String(data.team.totalApproved)}
                badge={data.team.declinedCount > 0 ? `${data.team.declinedCount} declinados` : undefined}
              />
              <BigCard
                icon={<DollarSign size={14} className="text-violet-500" />}
                label="Ticket médio"
                value={formatBRL(data.team.averageTicket)}
              />
              <BigCard
                icon={<TrendingUp size={14} className="text-warning" />}
                label="Fat. a mais na base"
                value={formatBRL(data.team.revenueExtra)}
                valueColor="text-warning"
              />
            </div>

            {data.team.topPlans.length > 0 && (
              <div className="bg-surface border border-line rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Trophy size={14} className="text-warning" />
                  <h3 className="text-xs font-bold uppercase tracking-wider text-primary">
                    Top 3 planos do mês
                  </h3>
                </div>
                <div className="divide-y divide-line">
                  {data.team.topPlans.map((p, idx) => (
                    <div key={p.erpId} className="flex items-center py-2">
                      <span className="text-xs text-muted w-6">#{idx + 1}</span>
                      <span className="text-sm text-fg flex-1 truncate">
                        {p.descricao}
                      </span>
                      <span className="text-xs font-bold bg-hover text-fg px-2 py-0.5 rounded-full">
                        {p.count}x
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* Vendedores */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Users size={14} className="text-primary" />
              <h2 className="text-xs font-bold uppercase tracking-wider text-primary">
                Vendedores ({data.sellers.length})
              </h2>
            </div>

            {data.sellers.length === 0 ? (
              <div className="bg-surface border border-line rounded-2xl p-6 text-center text-sm text-muted">
                Sem vendas aprovadas neste mês.
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {data.sellers.map((s) => <SellerCard key={s.vendedorId} seller={s} />)}
              </div>
            )}
          </section>

          {/* Gráfico YTD */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 size={14} className="text-primary" />
              <h2 className="text-xs font-bold uppercase tracking-wider text-primary">
                Vendas por vendedor — ano em curso
              </h2>
            </div>
            <div className="bg-surface border border-line rounded-2xl p-4">
              {data.monthlyBars.series.length === 0 ? (
                <p className="text-center text-sm text-muted py-6">
                  Sem dados no ano em curso.
                </p>
              ) : (
                <StackedBarChart
                  months={data.monthlyBars.months}
                  series={data.monthlyBars.series}
                />
              )}
            </div>
          </section>

          {/* Gráfico linha: crescimento da base + faturamento */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp size={14} className="text-primary" />
              <h2 className="text-xs font-bold uppercase tracking-wider text-primary">
                Crescimento no ano — base e faturamento
              </h2>
            </div>
            <div className="bg-surface border border-line rounded-2xl p-4">
              {data.yearlyGrowth.months.length === 0 ? (
                <p className="text-center text-sm text-muted py-6">
                  Sem dados no ano em curso.
                </p>
              ) : (
                <GrowthLineChart
                  months={data.yearlyGrowth.months}
                  clientsCumulative={data.yearlyGrowth.clientsCumulative}
                  revenueCumulative={data.yearlyGrowth.revenueCumulative}
                />
              )}
            </div>
          </section>

          {/* Contratos declinados do mês */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <XCircle size={14} className="text-danger" />
              <h2 className="text-xs font-bold uppercase tracking-wider text-danger">
                Declinados no mês ({data.declined.length})
              </h2>
            </div>
            {data.declined.length === 0 ? (
              <div className="bg-surface border border-line rounded-2xl p-6 text-center text-sm text-muted">
                Nenhum contrato declinado neste mês.
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {data.declined.map((d) => (
                  <div
                    key={d.id}
                    className="bg-surface border border-line rounded-2xl p-4"
                  >
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <p className="font-semibold text-fg truncate flex-1">
                        {d.clienteNome}
                      </p>
                      {d.declinedAt && (
                        <span className="text-[11px] text-muted whitespace-nowrap">
                          {new Date(d.declinedAt).toLocaleDateString('pt-BR')}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-fg-2 mb-2 truncate">
                      Vendedor: {d.vendedorNome}
                    </p>
                    <div className="bg-danger-soft/20 border border-danger-line/40 rounded-xl px-3 py-2">
                      <p className="text-[10px] uppercase font-bold text-danger mb-0.5">
                        Motivo
                      </p>
                      <p className="text-xs text-fg-2">
                        {d.declineReason || 'Motivo não informado'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function BigCard({
  icon, label, value, valueColor, badge,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  valueColor?: string;
  badge?: string;
}) {
  return (
    <div className="bg-surface border border-line rounded-2xl p-4">
      <div className="flex items-center gap-1.5 mb-1.5">
        {icon}
        <span className="text-[11px] font-medium uppercase text-fg-2">
          {label}
        </span>
      </div>
      <div className={`font-bold text-2xl ${valueColor ?? 'text-fg'}`}>
        {value}
      </div>
      {badge && (
        <div className="text-[11px] text-danger mt-1">{badge}</div>
      )}
    </div>
  );
}

function SellerCard({ seller }: { seller: FollowupSeller }) {
  return (
    <div className="bg-surface border border-line rounded-2xl p-4">
      <div className="flex items-center justify-between mb-3 gap-2">
        <p className="font-semibold text-fg truncate flex-1">
          {seller.vendedorNome}
        </p>
        {seller.declinedCount > 0 && (
          <span className="bg-danger-soft/30 text-danger text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap">
            {seller.declinedCount} declinado{seller.declinedCount === 1 ? '' : 's'}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2 mb-2 text-xs">
        <MiniStat label="Vendas no mês" value={String(seller.totalApproved)} valueColor="text-success" />
        <MiniStat label="Ticket médio" value={formatBRL(seller.averageTicket)} />
      </div>

      <div className="bg-canvas/60 rounded-lg px-2.5 py-1.5 mb-2">
        <div className="text-[10px] uppercase text-muted">Fat. a mais (mês)</div>
        <div className="text-warning font-bold text-sm">
          {formatBRL(seller.revenueExtra)}
        </div>
      </div>

      {seller.topPlan ? (
        <div className="bg-canvas/60 rounded-lg px-2.5 py-1.5 flex items-center gap-2">
          <Trophy size={11} className="text-warning flex-shrink-0" />
          <span className="text-[11px] text-fg-2 flex-1 truncate">
            <span className="text-warning font-medium">Top: </span>
            {seller.topPlan.descricao}
          </span>
          <span className="text-[10px] font-bold bg-hover text-fg px-1.5 py-0.5 rounded">
            {seller.topPlan.count}x
          </span>
        </div>
      ) : (
        <p className="text-[11px] italic text-faint">Sem plano associado</p>
      )}
    </div>
  );
}

function MiniStat({
  label, value, valueColor,
}: { label: string; value: string; valueColor?: string }) {
  return (
    <div className="bg-canvas/60 rounded-lg px-2 py-1.5">
      <div className="text-[10px] uppercase text-muted">{label}</div>
      <div className={`font-bold ${valueColor ?? 'text-fg'}`}>{value}</div>
    </div>
  );
}
